import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Chip,
  IconButton,
} from '@mui/material';
import {
  Store as StoreIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../../store';
import {
  registerMerchant,
  uploadVerificationDocument,
  clearRegistrationState,
  MerchantRegistrationData,
} from '../../store/slices/merchantSlice';

const steps = ['Business Information', 'Business Documents', 'Review & Submit'];

const businessTypes = [
  'Sole Proprietorship',
  'Partnership',
  'Limited Liability Company (LLC)',
  'Corporation',
  'Non-Profit',
  'Other',
];

const MerchantApplicationPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { profile, registrationLoading, registrationError, registrationSuccess } = useSelector(
    (state: RootState) => state.merchant
  );

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<MerchantRegistrationData>({
    businessName: '',
    email: '',
    phoneNumber: '',
    address: '',
    city: '',
    country: '',
    businessType: '',
    taxId: '',
  });
  const [documents, setDocuments] = useState<{ file: File; type: string }[]>([]);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [uploadingDocs, setUploadingDocs] = useState(false);

  useEffect(() => {
    return () => {
      dispatch(clearRegistrationState());
    };
  }, [dispatch]);

  // If already a merchant, redirect
  useEffect(() => {
    if (profile) {
      if (profile.status === 'approved') {
        navigate('/merchant/dashboard');
      } else if (profile.status === 'pending') {
        setActiveStep(2); // Show review step with pending status
      }
    }
  }, [profile, navigate]);

  // On successful registration
  useEffect(() => {
    if (registrationSuccess && activeStep === 2) {
      // Stay on review step showing success
    }
  }, [registrationSuccess, activeStep]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };

  const validateStep1 = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.businessName.trim()) errors.businessName = 'Business name is required';
    if (!formData.email.trim()) errors.email = 'Business email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Invalid email format';
    if (!formData.phoneNumber.trim()) errors.phoneNumber = 'Phone number is required';
    if (!formData.address.trim()) errors.address = 'Business address is required';
    if (!formData.city.trim()) errors.city = 'City is required';
    if (!formData.country.trim()) errors.country = 'Country is required';
    if (!formData.businessType) errors.businessType = 'Business type is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateStep2 = (): boolean => {
    if (documents.length === 0) {
      setFormErrors({ documents: 'At least one business document is required' });
      return false;
    }
    setFormErrors({});
    return true;
  };

  const handleNext = () => {
    if (activeStep === 0 && !validateStep1()) return;
    if (activeStep === 1 && !validateStep2()) return;
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>, docType: string) => {
    const files = event.target.files;
    if (files) {
      const newDocs = Array.from(files).map(file => ({ file, type: docType }));
      setDocuments(prev => [...prev, ...newDocs]);
      if (formErrors.documents) {
        setFormErrors(prev => {
          const updated = { ...prev };
          delete updated.documents;
          return updated;
        });
      }
    }
  };

  const handleRemoveDocument = (index: number) => {
    setDocuments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    try {
      // Step 1: Register merchant
      const result = await dispatch(registerMerchant(formData)).unwrap();

      // Step 2: Upload documents
      if (documents.length > 0) {
        setUploadingDocs(true);
        for (const doc of documents) {
          const fd = new FormData();
          fd.append('document', doc.file);
          fd.append('documentType', doc.type);
          await dispatch(uploadVerificationDocument(fd)).unwrap();
        }
        setUploadingDocs(false);
      }
    } catch (error) {
      setUploadingDocs(false);
      // Error handled by Redux
    }
  };

  const renderStep1 = () => (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <BusinessIcon sx={{ color: '#11C1EC' }} />
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#202647' }}>
          Business Information
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ color: '#7F839C', mb: 3 }}>
        Provide your business details to get started with MalPay Merchant services. All fields marked are required for verification.
      </Typography>

      <Grid container spacing={2.5}>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label="Business Name"
            value={formData.businessName}
            onChange={(e) => handleInputChange('businessName', e.target.value)}
            error={!!formErrors.businessName}
            helperText={formErrors.businessName}
            placeholder="e.g. Acme Trading Ltd."
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Business Email"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            error={!!formErrors.email}
            helperText={formErrors.email}
            placeholder="business@example.com"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Phone Number"
            value={formData.phoneNumber}
            onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
            error={!!formErrors.phoneNumber}
            helperText={formErrors.phoneNumber}
            placeholder="+234 800 000 0000"
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <FormControl fullWidth error={!!formErrors.businessType}>
            <InputLabel>Business Type</InputLabel>
            <Select
              value={formData.businessType}
              onChange={(e) => handleInputChange('businessType', e.target.value)}
              label="Business Type"
            >
              {businessTypes.map((type) => (
                <MenuItem key={type} value={type}>{type}</MenuItem>
              ))}
            </Select>
            {formErrors.businessType && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                {formErrors.businessType}
              </Typography>
            )}
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label="Business Address"
            multiline
            rows={2}
            value={formData.address}
            onChange={(e) => handleInputChange('address', e.target.value)}
            error={!!formErrors.address}
            helperText={formErrors.address}
            placeholder="Street address"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="City"
            value={formData.city}
            onChange={(e) => handleInputChange('city', e.target.value)}
            error={!!formErrors.city}
            helperText={formErrors.city}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Country"
            value={formData.country}
            onChange={(e) => handleInputChange('country', e.target.value)}
            error={!!formErrors.country}
            helperText={formErrors.country}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label="Tax ID / Registration Number (Optional)"
            value={formData.taxId}
            onChange={(e) => handleInputChange('taxId', e.target.value)}
            placeholder="e.g. TIN-12345678"
          />
        </Grid>
      </Grid>
    </Box>
  );

  const renderStep2 = () => (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <DescriptionIcon sx={{ color: '#11C1EC' }} />
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#202647' }}>
          Business Documents
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }} icon={<InfoIcon />}>
        <Typography variant="body2">
          Business documents are required to verify your identity and business legitimacy.
          This helps protect you and your customers, and is required by financial regulations.
        </Typography>
      </Alert>

      {/* Document upload sections */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Business Registration */}
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #E8E8F0' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#202647', mb: 1 }}>
            Business Registration Certificate
          </Typography>
          <Typography variant="body2" sx={{ color: '#7F839C', mb: 2 }}>
            CAC certificate, business license, or equivalent registration document.
          </Typography>
          <Button
            variant="outlined"
            component="label"
            startIcon={<CloudUploadIcon />}
            sx={{
              borderColor: '#11C1EC',
              color: '#11C1EC',
              borderRadius: 2,
              '&:hover': { borderColor: '#09486D', backgroundColor: 'rgba(17, 193, 236, 0.05)' },
            }}
          >
            Upload Document
            <input
              type="file"
              hidden
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => handleFileUpload(e, 'cac_certificate')}
            />
          </Button>
        </Paper>

        {/* Proof of Address */}
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #E8E8F0' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#202647', mb: 1 }}>
            Proof of Business Address
          </Typography>
          <Typography variant="body2" sx={{ color: '#7F839C', mb: 2 }}>
            Utility bill, bank statement, or lease agreement showing your business address (dated within 3 months).
          </Typography>
          <Button
            variant="outlined"
            component="label"
            startIcon={<CloudUploadIcon />}
            sx={{
              borderColor: '#11C1EC',
              color: '#11C1EC',
              borderRadius: 2,
              '&:hover': { borderColor: '#09486D', backgroundColor: 'rgba(17, 193, 236, 0.05)' },
            }}
          >
            Upload Document
            <input
              type="file"
              hidden
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => handleFileUpload(e, 'proof_of_address')}
            />
          </Button>
        </Paper>

        {/* ID Document */}
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #E8E8F0' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#202647', mb: 1 }}>
            Owner/Director ID Document
          </Typography>
          <Typography variant="body2" sx={{ color: '#7F839C', mb: 2 }}>
            Government-issued ID (passport, national ID, or driver's license) of the business owner or director.
          </Typography>
          <Button
            variant="outlined"
            component="label"
            startIcon={<CloudUploadIcon />}
            sx={{
              borderColor: '#11C1EC',
              color: '#11C1EC',
              borderRadius: 2,
              '&:hover': { borderColor: '#09486D', backgroundColor: 'rgba(17, 193, 236, 0.05)' },
            }}
          >
            Upload Document
            <input
              type="file"
              hidden
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={(e) => handleFileUpload(e, 'directors_id')}
            />
          </Button>
        </Paper>
      </Box>

      {/* Uploaded documents list */}
      {documents.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#202647', mb: 1.5 }}>
            Uploaded Documents ({documents.length})
          </Typography>
          {documents.map((doc, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1.5,
                px: 2,
                mb: 1,
                borderRadius: 2,
                bgcolor: 'rgba(17, 193, 236, 0.04)',
                border: '1px solid rgba(17, 193, 236, 0.15)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <DescriptionIcon sx={{ color: '#11C1EC', fontSize: 20 }} />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#202647' }}>
                    {doc.file.name}
                  </Typography>
                  <Chip
                    label={doc.type.replace(/_/g, ' ')}
                    size="small"
                    sx={{
                      mt: 0.5,
                      fontSize: '0.7rem',
                      height: 20,
                      bgcolor: 'rgba(17, 193, 236, 0.1)',
                      color: '#09486D',
                    }}
                  />
                </Box>
              </Box>
              <IconButton size="small" onClick={() => handleRemoveDocument(index)} sx={{ color: '#FF4757' }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {formErrors.documents && (
        <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
          {formErrors.documents}
        </Alert>
      )}
    </Box>
  );

  const renderStep3 = () => {
    // If already registered and pending
    if (profile?.status === 'pending' || registrationSuccess) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(17, 193, 236, 0.15), rgba(9, 72, 109, 0.1))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 40, color: '#11C1EC' }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#202647', mb: 1 }}>
            Application Submitted
          </Typography>
          <Typography variant="body1" sx={{ color: '#7F839C', mb: 3, maxWidth: 450, mx: 'auto' }}>
            Your merchant application is under review. Our team will verify your documents and get back to you
            within 1-3 business days. You'll receive a notification once approved.
          </Typography>
          <Chip
            label="Awaiting Verification"
            sx={{
              fontSize: '0.85rem',
              py: 2.5,
              px: 1,
              bgcolor: 'rgba(255, 165, 0, 0.1)',
              color: '#E88A00',
              fontWeight: 600,
              borderRadius: 2,
            }}
          />
          <Box sx={{ mt: 4 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/dashboard')}
              sx={{
                borderColor: '#11C1EC',
                color: '#11C1EC',
                borderRadius: 2,
                px: 4,
                '&:hover': { borderColor: '#09486D', bgcolor: 'rgba(17, 193, 236, 0.05)' },
              }}
            >
              Back to Dashboard
            </Button>
          </Box>
        </Box>
      );
    }

    // If rejected
    if (profile?.status === 'rejected') {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2, textAlign: 'left' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Application Rejected</Typography>
            <Typography variant="body2">
              {profile.rejectionReason || 'Your application was not approved. Please review and resubmit with correct information.'}
            </Typography>
          </Alert>
          <Button
            variant="contained"
            onClick={() => setActiveStep(0)}
            sx={{
              background: 'linear-gradient(135deg, #11C1EC, #09486D)',
              borderRadius: 2,
              px: 4,
              '&:hover': { background: 'linear-gradient(135deg, #0DB5DE, #07375A)' },
            }}
          >
            Resubmit Application
          </Button>
        </Box>
      );
    }

    // Review & Submit
    return (
      <Box>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon sx={{ color: '#11C1EC' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#202647' }}>
            Review Your Application
          </Typography>
        </Box>

        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E8E8F0', mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#11C1EC', mb: 2, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
            Business Details
          </Typography>
          <Grid container spacing={2}>
            {[
              { label: 'Business Name', value: formData.businessName },
              { label: 'Email', value: formData.email },
              { label: 'Phone', value: formData.phoneNumber },
              { label: 'Type', value: formData.businessType },
              { label: 'Address', value: formData.address },
              { label: 'City', value: formData.city },
              { label: 'Country', value: formData.country },
              { label: 'Tax ID', value: formData.taxId || 'Not provided' },
            ].map((item) => (
              <Grid size={{ xs: 12, sm: 6 }} key={item.label}>
                <Typography variant="caption" sx={{ color: '#7F839C' }}>{item.label}</Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#202647' }}>{item.value}</Typography>
              </Grid>
            ))}
          </Grid>
        </Paper>

        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #E8E8F0', mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#11C1EC', mb: 2, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 1 }}>
            Documents ({documents.length})
          </Typography>
          {documents.map((doc, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <DescriptionIcon sx={{ color: '#7F839C', fontSize: 18 }} />
              <Typography variant="body2" sx={{ color: '#202647' }}>{doc.file.name}</Typography>
              <Chip label={doc.type.replace(/_/g, ' ')} size="small" sx={{ fontSize: '0.7rem', height: 20 }} />
            </Box>
          ))}
        </Paper>

        {registrationError && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {registrationError}
          </Alert>
        )}
      </Box>
    );
  };

  const isSubmitting = registrationLoading || uploadingDocs;

  return (
    <Box sx={{ maxWidth: { xs: '100%', sm: 720 }, mx: 'auto', px: { xs: 1, sm: 0 } }}>
      {/* Header */}
      <Box sx={{ mb: { xs: 2, sm: 4 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <StoreIcon sx={{ fontSize: { xs: 28, sm: 32 }, color: '#11C1EC' }} />
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#202647', fontSize: { xs: '1.5rem', sm: '2rem' } }}>
            Merchant Application
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ color: '#7F839C', fontSize: { xs: '0.85rem', sm: '1rem' } }}>
          Apply to become a MalPay merchant and start accepting payments for your business.
        </Typography>
      </Box>

      {/* Stepper */}
      <Stepper
        activeStep={activeStep}
        alternativeLabel={false}
        sx={{
          mb: { xs: 2, sm: 4 },
          '& .MuiStepIcon-root.Mui-active': { color: '#11C1EC' },
          '& .MuiStepIcon-root.Mui-completed': { color: '#09486D' },
          '& .MuiStepLabel-label': { fontSize: { xs: '0.75rem', sm: '0.875rem' } },
        }}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Form content */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 4 },
          borderRadius: 4,
          border: '1px solid rgba(32, 38, 71, 0.06)',
          boxShadow: '0 4px 20px rgba(32, 38, 71, 0.04)',
        }}
      >
        {activeStep === 0 && renderStep1()}
        {activeStep === 1 && renderStep2()}
        {activeStep === 2 && renderStep3()}

        {/* Navigation buttons */}
        {!(profile?.status === 'pending' || registrationSuccess) && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                sx={{ color: '#565972', '&:hover': { bgcolor: 'rgba(17, 193, 236, 0.05)' } }}
              >
                Back
              </Button>
              {activeStep < 2 ? (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  sx={{
                    background: 'linear-gradient(135deg, #11C1EC, #09486D)',
                    borderRadius: 2,
                    px: 4,
                    '&:hover': { background: 'linear-gradient(135deg, #0DB5DE, #07375A)' },
                  }}
                >
                  Continue
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : undefined}
                  sx={{
                    background: 'linear-gradient(135deg, #11C1EC, #09486D)',
                    borderRadius: 2,
                    px: 4,
                    '&:hover': { background: 'linear-gradient(135deg, #0DB5DE, #07375A)' },
                  }}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Application'}
                </Button>
              )}
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default MerchantApplicationPage;
